import {
  GuidanceTourConfig,
  GuidanceTourStepCompletionMap,
  GuidanceTourStorageState,
  GuidanceTourStatus,
  TourProgressSummary,
} from '../types';

const buildKey = (tourId: string, dealerId: string) =>
  `guidance-tour:${tourId}:${dealerId}`;

export const initStepCompleted = (
  config: GuidanceTourConfig,
): GuidanceTourStepCompletionMap =>
  Object.fromEntries(config.steps.map(step => [step.id, false]));

export const isStepCompleted = (
  stepCompleted: GuidanceTourStepCompletionMap,
  stepId: string,
): boolean => stepCompleted[stepId] === true;

const migrateLegacyStepStatuses = (
  raw: Record<string, unknown> | undefined,
  config: GuidanceTourConfig,
): GuidanceTourStepCompletionMap | null => {
  if (!raw) return null;
  return Object.fromEntries(
    config.steps.map(step => [
      step.id,
      raw[step.id] === 'completed' || raw[step.id] === true,
    ]),
  );
};

const normalizeStepCompleted = (
  config: GuidanceTourConfig,
  raw: Partial<GuidanceTourStorageState> & {
    stepStatuses?: Record<string, string>;
  } | null,
  dealerId: string,
): GuidanceTourStepCompletionMap => {
  const base = initStepCompleted(config);

  if (raw?.stepCompleted) {
    return { ...base, ...raw.stepCompleted };
  }

  const migrated = migrateLegacyStepStatuses(
    raw?.stepStatuses as Record<string, unknown> | undefined,
    config,
  );
  if (migrated) return migrated;

  return base;
};

const normalizeStorage = (
  config: GuidanceTourConfig,
  raw: Partial<GuidanceTourStorageState> & {
    stepStatuses?: Record<string, string>;
  } | null,
  dealerId: string,
): GuidanceTourStorageState => {
  const stepCompleted = normalizeStepCompleted(config, raw, dealerId);

  config.steps.forEach(step => {
    if (!(step.id in stepCompleted)) {
      stepCompleted[step.id] = false;
    }
  });

  const finalStepIndex = config.steps.length - 1;
  const finalStep = config.steps[finalStepIndex];
  if (finalStep && isStepCompleted(stepCompleted, finalStep.id)) {
    const hasIncompletePrior = config.steps
      .slice(0, finalStepIndex)
      .some(step => !isStepCompleted(stepCompleted, step.id));
    if (hasIncompletePrior) {
      stepCompleted[finalStep.id] = false;
    }
  }

  return {
    status: raw?.status ?? 'active',
    currentStepIndex: raw?.currentStepIndex ?? 0,
    dealerId,
    updatedAt: raw?.updatedAt ?? Date.now(),
    stepCompleted,
    stepContext: raw?.stepContext ?? {},
    stepAccessibility: raw?.stepAccessibility ?? {},
    lastViewedStepId: raw?.lastViewedStepId,
    hasPausedSession: raw?.hasPausedSession,
    hasLaunchReadinessIntroAcknowledged: raw?.hasLaunchReadinessIntroAcknowledged,
  };
};

export const mapApiProgressToTourStorage = (
  config: GuidanceTourConfig,
  dealerId: string,
  raw: Partial<GuidanceTourStorageState> & {
    status: GuidanceTourStatus;
    currentStepIndex: number;
  },
): GuidanceTourStorageState =>
  normalizeStorage(
    config,
    {
      status: raw.status,
      currentStepIndex: raw.currentStepIndex,
      stepCompleted: raw.stepCompleted,
      stepContext: raw.stepContext,
      stepAccessibility: raw.stepAccessibility,
      lastViewedStepId: raw.lastViewedStepId,
      hasPausedSession: raw.hasPausedSession,
      hasLaunchReadinessIntroAcknowledged: raw.hasLaunchReadinessIntroAcknowledged,
      updatedAt: raw.updatedAt,
    },
    dealerId,
  );

export const findNextIncompleteIndex = (
  config: GuidanceTourConfig,
  stepCompleted: GuidanceTourStepCompletionMap,
  fromIndex: number,
): number | null => {
  for (let i = fromIndex + 1; i < config.steps.length; i += 1) {
    const step = config.steps[i];
    if (!isStepCompleted(stepCompleted, step.id)) return i;
  }
  return null;
};

export const findPreviousIncompleteIndex = (
  config: GuidanceTourConfig,
  stepCompleted: GuidanceTourStepCompletionMap,
  fromIndex: number,
): number | null => {
  for (let i = fromIndex - 1; i >= 0; i -= 1) {
    const step = config.steps[i];
    if (!isStepCompleted(stepCompleted, step.id)) return i;
  }
  return null;
};

export const findFirstIncompleteIndex = (
  config: GuidanceTourConfig,
  stepCompleted: GuidanceTourStepCompletionMap,
): number => {
  const index = config.steps.findIndex(
    step => !isStepCompleted(stepCompleted, step.id),
  );
  return index >= 0 ? index : config.steps.length - 1;
};

/** Resume from the last step the user was viewing (save & exit / paused session). */
export const findResumeStepIndex = (
  config: GuidanceTourConfig,
  storage: GuidanceTourStorageState,
): number => {
  if (storage.lastViewedStepId) {
    const indexById = config.steps.findIndex(
      step => step.id === storage.lastViewedStepId,
    );
    if (indexById >= 0) {
      return indexById;
    }
  }

  return Math.min(
    Math.max(storage.currentStepIndex, 0),
    config.steps.length - 1,
  );
};

export const areAllStepsCompleted = (
  config: GuidanceTourConfig,
  stepCompleted: GuidanceTourStepCompletionMap,
): boolean =>
  config.steps.every(step => isStepCompleted(stepCompleted, step.id));

export const countIncompletePriorSteps = (
  config: GuidanceTourConfig,
  stepCompleted: GuidanceTourStepCompletionMap,
  stepIndex: number,
): number => {
  let count = 0;
  for (let i = 0; i < stepIndex; i += 1) {
    const step = config.steps[i];
    if (!isStepCompleted(stepCompleted, step.id)) {
      count += 1;
    }
  }
  return count;
};

/** Final tour step can only be marked once every earlier step is completed. */
export const canMarkStepComplete = (
  config: GuidanceTourConfig,
  stepCompleted: GuidanceTourStepCompletionMap,
  stepIndex: number,
): boolean => {
  const step = config.steps[stepIndex];
  if (!step || isStepCompleted(stepCompleted, step.id)) {
    return false;
  }

  const isFinalStep = stepIndex === config.steps.length - 1;
  if (!isFinalStep) {
    return true;
  }

  return countIncompletePriorSteps(config, stepCompleted, stepIndex) === 0;
};

export const getTourStorage = (
  tourId: string,
  dealerId: string,
  config?: GuidanceTourConfig,
): GuidanceTourStorageState | null => {
  try {
    const raw = localStorage.getItem(buildKey(tourId, dealerId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<GuidanceTourStorageState> & {
      stepStatuses?: Record<string, string>;
    };
    if (!config) return parsed as GuidanceTourStorageState;
    return normalizeStorage(config, parsed, dealerId);
  } catch {
    return null;
  }
};

export const saveTourStorage = (
  tourId: string,
  dealerId: string,
  state: Partial<GuidanceTourStorageState> & {
    status: GuidanceTourStatus;
    currentStepIndex: number;
  },
  config?: GuidanceTourConfig,
): GuidanceTourStorageState => {
  const existing = getTourStorage(tourId, dealerId, config);
  const payload: GuidanceTourStorageState = normalizeStorage(
    config ?? { id: tourId, steps: [] },
    {
      ...existing,
      ...state,
      dealerId,
      updatedAt: Date.now(),
      stepCompleted: {
        ...(existing?.stepCompleted ?? {}),
        ...(state.stepCompleted ?? {}),
      },
    },
    dealerId,
  );

  if (config) {
    payload.stepCompleted = {
      ...initStepCompleted(config),
      ...payload.stepCompleted,
    };
  }

  localStorage.setItem(buildKey(tourId, dealerId), JSON.stringify(payload));
  return payload;
};

export const resetTourProgress = (
  tourId: string,
  dealerId: string,
  config: GuidanceTourConfig,
): GuidanceTourStorageState =>
  saveTourStorage(
    tourId,
    dealerId,
    {
      status: 'active',
      currentStepIndex: 0,
      stepCompleted: initStepCompleted(config),
      lastViewedStepId: config.steps[0]?.id,
      hasPausedSession: false,
    },
    config,
  );

export const clearTourStorage = (tourId: string, dealerId: string): void => {
  localStorage.removeItem(buildKey(tourId, dealerId));
};

export const getTourProgress = (
  config: GuidanceTourConfig,
  storage: GuidanceTourStorageState | null,
): TourProgressSummary => {
  const total = config.steps.length;
  const stepCompleted = storage?.stepCompleted ?? {};
  let completed = 0;

  config.steps.forEach(step => {
    if (isStepCompleted(stepCompleted, step.id)) {
      completed += 1;
    }
  });

  const lastStepIndex = Math.max(total - 1, 0);
  const lastStep = config.steps[lastStepIndex];
  const allPriorStepsComplete =
    total > 0 &&
    config.steps
      .slice(0, lastStepIndex)
      .every(step => isStepCompleted(stepCompleted, step.id));
  const onLastStep =
    (storage?.currentStepIndex ?? 0) === lastStepIndex ||
    storage?.lastViewedStepId === lastStep?.id;
  const tourFinished =
    storage?.status === 'completed' || completed === total;

  const displayCompleted = tourFinished
    ? total
    : allPriorStepsComplete && onLastStep
      ? total
      : completed;
  const pending = Math.max(total - displayCompleted, 0);
  const percent =
    tourFinished || (allPriorStepsComplete && onLastStep)
      ? 100
      : total > 0
        ? Math.round((completed / total) * 100)
        : 0;
  const nextIncompleteIndex = storage
    ? findFirstIncompleteIndex(config, storage.stepCompleted)
    : 0;

  return {
    completed: displayCompleted,
    pending,
    total,
    percent,
    nextIncompleteIndex,
  };
};

export const isTourResumable = (
  tourId: string,
  dealerId: string,
  config: GuidanceTourConfig,
): GuidanceTourStorageState | null => {
  const stored = getTourStorage(tourId, dealerId, config);
  if (!stored || stored.status !== 'active') return null;

  const progress = getTourProgress(config, stored);
  const hasAnyProgress =
    Boolean(stored.hasPausedSession) ||
    progress.completed > 0 ||
    stored.currentStepIndex > 0;

  if (!hasAnyProgress) return null;
  return stored;
};

export const hasSavedTourProgress = (
  tourId: string,
  dealerId: string,
  config: GuidanceTourConfig,
): boolean => {
  const stored = getTourStorage(tourId, dealerId, config);
  if (!stored || stored.status !== 'active') return false;
  return isTourResumable(tourId, dealerId, config) !== null;
};

export const readLauncherProgress = (
  tourId: string,
  dealerId: string,
  config: GuidanceTourConfig,
) => {
  const stored = getTourStorage(tourId, dealerId, config);
  const progress = getTourProgress(config, stored);
  const hasProgress = hasSavedTourProgress(tourId, dealerId, config);
  return { stored, progress, hasProgress };
};

export const markStepCompleted = (
  tourId: string,
  dealerId: string,
  config: GuidanceTourConfig,
  stepId: string,
  completed: boolean,
  currentStepIndex: number,
): GuidanceTourStorageState =>
  saveTourStorage(
    tourId,
    dealerId,
    {
      status: 'active',
      currentStepIndex,
      stepCompleted: { [stepId]: completed },
      lastViewedStepId: stepId,
      hasPausedSession: true,
    },
    config,
  );

export const markTourStatus = (
  tourId: string,
  dealerId: string,
  config: GuidanceTourConfig,
  status: GuidanceTourStatus,
  currentStepIndex: number,
  stepCompleted?: GuidanceTourStepCompletionMap,
): GuidanceTourStorageState =>
  saveTourStorage(
    tourId,
    dealerId,
    {
      status,
      currentStepIndex,
      ...(stepCompleted ? { stepCompleted } : {}),
    },
    config,
  );
